import React, { useEffect, useState } from "react";
import { Link } from "react-router";

import DashboardIcon from "@mui/icons-material/Dashboard";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ForumIcon from "@mui/icons-material/Forum";
import GroupIcon from "@mui/icons-material/Group";
import GroupsIcon from "@mui/icons-material/Groups";
import LayersIcon from "@mui/icons-material/Layers";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import MoreIcon from "@mui/icons-material/More";
import SettingsIcon from "@mui/icons-material/Settings";
import StorageIcon from "@mui/icons-material/Storage";
import PieChartIcon from "@mui/icons-material/PieChart";
import ReplayIcon from "@mui/icons-material/Replay";

import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Badge from "@mui/material/Badge";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import ListItemButton from "@mui/material/ListItemButton";

import { useUserAuth } from "./contexts/authContext";
import { useEnv } from "./contexts/envContext";
import { useChat } from "./contexts/chatContext";
import { Permissions } from "./permissionUtil";

import "./css/cmp.css";

export function LeftNavigation({ open, setOpen }) {
    const { user } = useUserAuth();
    const { totalUnreadMessages } = useChat();
    const { dataLoggingOn } = useEnv();

    const [expanded, setExpanded] = useState(false);

    const isDesktop = useMediaQuery("(min-width:900px)");

    const handleChange = (panel) => (event, isExpanded) => {
        if (!open) setOpen(true);
        setExpanded(isExpanded ? panel : false);
    };

    const handleClick = () => {
        // close after click on mobile
        if (isDesktop) return false;
        else setOpen(false);
    };

    useEffect(() => {
        if (!open) setExpanded(false);
    }, [open]);

    return (
        <Box sx={{ width: "240px", overflowY: "auto", height: "100%", overflowX: "hidden" }}>
            {isDesktop === false ? <Toolbar /> : <></>}

            <Divider />

            <List component="nav" className="nav">
                {Permissions.airspaces.hasPermission(user) && (
                    <Accordion sx={{ width: "100%", display: "grid" }} onChange={handleChange("panel1")} expanded={expanded === "panel1"} disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                            <Tooltip title="Airspaces" placement="right">
                                <ListItemIcon sx={{ pl: "4px" }}>
                                    <DashboardIcon />
                                </ListItemIcon>
                            </Tooltip>
                            <ListItemText primary="Airspaces" />
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            <Link to="/" onClick={handleClick}>
                                <ListItemButton sx={{ pl: "72px" }}>
                                    <ListItemText primary={"Airspace Manager"} />
                                </ListItemButton>
                            </Link>
                            {Permissions.counterUAS.hasPermission(user) && (
                                <Link to="/asset-map" onClick={handleClick}>
                                    <ListItemButton sx={{ pl: "72px" }}>
                                        <ListItemText primary="Asset Monitoring" />
                                    </ListItemButton>
                                </Link>
                            )}
                            <Link to="/operations" onClick={handleClick}>
                                <ListItemButton data-testid="PushOperations" sx={{ pl: "72px" }}>
                                    <ListItemText primary="Operation Planning" slotProps={{ primary: { noWrap: true } }} />
                                </ListItemButton>
                            </Link>
                            <Link to="/dashboard" onClick={handleClick}>
                                <ListItemButton sx={{ pl: "72px" }}>
                                    <ListItemText primary="Pilot Dashboard" />
                                </ListItemButton>
                            </Link>
                            {Permissions.infrastructure.hasPermission(user) && (
                                <Link to="/infrastructure" onClick={handleClick}>
                                    <ListItemButton sx={{ pl: "72px" }}>
                                        <ListItemText primary="Infrastructure" />
                                    </ListItemButton>
                                </Link>
                            )}
                        </AccordionDetails>
                    </Accordion>
                )}
                <Link to="/map" onClick={handleClick}>
                    <ListItemButton>
                        <Tooltip title="Map" placement="right">
                            <ListItemIcon sx={{ pl: "4px" }}>
                                <LayersIcon />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText primary="Map" />
                    </ListItemButton>
                </Link>
                {Permissions.chat.hasPermission(user) && (
                    <Link to="/chat" onClick={handleClick}>
                        <ListItemButton>
                            <Tooltip title="Chat" placement="right">
                                <ListItemIcon sx={{ pl: "4px" }}>
                                    <Badge color="error" badgeContent={totalUnreadMessages}>
                                        <ForumIcon />
                                    </Badge>
                                </ListItemIcon>
                            </Tooltip>
                            <ListItemText primary="Chat" />
                        </ListItemButton>
                    </Link>
                )}
                {Permissions.manageUsers.hasPermission(user) && (
                    <Link to="/manage" onClick={handleClick}>
                        <ListItemButton>
                            <Tooltip title="Manage Users" placement="right">
                                <ListItemIcon sx={{ pl: "4px" }}>
                                    <GroupIcon />
                                </ListItemIcon>
                            </Tooltip>
                            <ListItemText primary="Manage Users" slotProps={{ primary: { noWrap: true } }} />
                        </ListItemButton>
                    </Link>
                )}
                {Permissions.manageOrganizations.hasPermission(user) && (
                    <Link to="/organizations" onClick={handleClick}>
                        <ListItemButton>
                            <Tooltip title="Manage Orgs" placement="right">
                                <ListItemIcon sx={{ pl: "4px" }}>
                                    <GroupsIcon />
                                </ListItemIcon>
                            </Tooltip>
                            <ListItemText primary="Manage Orgs" slotProps={{ primary: { noWrap: true } }} />
                        </ListItemButton>
                    </Link>
                )}
                {Permissions.settings.hasPermission(user) && (
                    <ListItem sx={{ padding: "0px !important" }}>
                        <Accordion sx={{ width: "100%", display: "grid" }} onChange={handleChange("panel2")} expanded={expanded === "panel2"} disableGutters>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                                <Tooltip title="System Settings" placement="right">
                                    <ListItemIcon sx={{ pl: "4px" }}>
                                        <SettingsIcon />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText primary="System Settings" slotProps={{ primary: { noWrap: true } }} />
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                                <Link to="/map-settings" onClick={handleClick}>
                                    <ListItemButton data-testid="MapSettings" sx={{ pl: "72px" }}>
                                        <ListItemText primary="Manage Settings" />
                                    </ListItemButton>
                                </Link>
                                <Link to="/drone-settings" onClick={handleClick}>
                                    <ListItemButton data-testid="ManageDrones" sx={{ pl: "72px" }}>
                                        <ListItemText primary="Manage Drones" />
                                    </ListItemButton>
                                </Link>
                                {Permissions.hmsSettings.hasPermission(user) && (
                                    <Link to="/hms-settings" onClick={handleClick}>
                                        <ListItemButton data-testid="ManageHMS" sx={{ pl: "72px" }}>
                                            <ListItemText primary="Manage HMS" />
                                        </ListItemButton>
                                    </Link>
                                )}
                                {Permissions.zcaSettings.hasPermission(user) && (
                                    <Link to="/zca-settings" onClick={handleClick}>
                                        <ListItemButton data-testid="ManageConfigs" sx={{ pl: "72px" }}>
                                            <ListItemText primary="Manage Configs" />
                                        </ListItemButton>
                                    </Link>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </ListItem>
                )}
                {Permissions.datalogs.hasPermission(user) && (
                    <Link to="/datalogs" onClick={handleClick}>
                        <ListItemButton>
                            <Tooltip title="Data Logs" placement="right">
                                <ListItemIcon sx={{ pl: "4px" }}>
                                    <MoreIcon />
                                </ListItemIcon>
                            </Tooltip>
                            <ListItemText primary="Data Logs" />
                        </ListItemButton>
                    </Link>
                )}
                {Permissions.monitoring.hasPermission(user, dataLoggingOn) && (
                    <Link to="/monitoring" onClick={handleClick}>
                        <ListItemButton>
                            <Tooltip title="Monitoring" placement="right">
                                <ListItemIcon sx={{ pl: "4px" }}>
                                    <StorageIcon />
                                </ListItemIcon>
                            </Tooltip>
                            <ListItemText primary="Monitoring" />
                        </ListItemButton>
                    </Link>
                )}
                {Permissions.hms.hasPermission(user) && (
                    <Link to="/hms" onClick={handleClick}>
                        <ListItemButton>
                            <Tooltip title="HIMS" placement="right">
                                <ListItemIcon sx={{ pl: "4px" }}>
                                    <MonitorHeartIcon />
                                </ListItemIcon>
                            </Tooltip>
                            <ListItemText primary="HIMS" />
                        </ListItemButton>
                    </Link>
                )}
                {Permissions.analysis.hasPermission(user, dataLoggingOn) && (
                    <Link to="/analysis" onClick={handleClick}>
                        <ListItemButton>
                            <Tooltip title="Analysis" placement="right">
                                <ListItemIcon sx={{ pl: "4px" }}>
                                    <PieChartIcon />
                                </ListItemIcon>
                            </Tooltip>
                            <ListItemText primary="Analysis" slotProps={{ primary: { noWrap: true } }} />
                        </ListItemButton>
                    </Link>
                )}
                {Permissions.playback.hasPermission(user) && (
                    <Link to="/playback" onClick={handleClick}>
                        <ListItemButton>
                            <ListItemIcon sx={{ pl: "4px" }}>
                                <ReplayIcon />
                            </ListItemIcon>
                            <ListItemText primary="Map Playback" primaryTypographyProps={{ noWrap: true }} />
                        </ListItemButton>
                    </Link>
                )}
            </List>
        </Box>
    );
}
